import { StrictMode, useSyncExternalStore } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "./lib/query-client";
import { router } from "./router";
import { useAuth } from "./lib/auth";
import { useAdminStore } from "./store";
import { setupSessionSync } from "./lib/session-sync";
import "./styles/app.css";

// Sibling-tab session convergence: adopt a sibling's token rotation into this
// tab's store, and propagate a sibling's sign-out through the full teardown.
setupSessionSync();

const { onFinishHydration, hasHydrated } = useAdminStore.persist;

function useHasHydrated(): boolean {
  return useSyncExternalStore(onFinishHydration, hasHydrated, () => false);
}

function App() {
  const hydrated = useHasHydrated();
  const auth = useAuth();

  if (!hydrated) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} context={{ auth }} />
      {import.meta.env.DEV && <ReactQueryDevtools buttonPosition="bottom-left" />}
    </QueryClientProvider>
  );
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
